<script setup lang="ts">
import type { Product } from '~/config'

interface Props {
  product: Product
}

const props = defineProps<Props>()

const icon = computed(() =>
  props.product.iconFile
    ? getSiteAsset({
        type: 'img',
        path: 'product|icon',
        fileName: props.product.iconFile,
      })
    : undefined
)

const productImgIds = computed(() => [
  `${props.product.code}-thumbnail`,
  `${props.product.code}-icon`,
])

const { allCompleted: isReady } = useImageLoaderTracker(productImgIds)

const img = computed(() => {
  const { bannerFile, thumbnailFile } = props.product
  const path = !!thumbnailFile ? 'product|thumbnail' : 'product|banner'
  return getSiteAsset({
    type: 'img',
    path,
    fileName: thumbnailFile ?? bannerFile,
  })
})
</script>

<template>
  <PvSkeleton
    v-if="!isReady"
    class="aspect-[4/3] w-full"
  />
  <ClientOnly>
    <PvButton
      v-show="isReady"
      class="p-0 shadow-md"
      severity="secondary"
      fluid
      as="a"
      :href="`/product/${product.code}`"
      :style="{
        '--p-button-secondary-background': '#ffffff',
      }"
    >
      <div class="aspect-[4/3] overflow-hidden">
        <ImageComponent
          :id="`${product.code}-thumbnail`"
          :src="img"
          :alt="product.name"
          class="object-cover h-[70%] md:h-[75%] w-full"
        />
        <div class="flex items-center justify-center gap-2 px-2 h-[30%] md:h-[25%]">
          <div>
            <CustomIcon
              v-if="icon"
              :id="`${product.code}-icon`"
              :icon-url="icon"
              class="size-6 accent"
            />
          </div>
          <span class="text-left text-xs md:text-sm leading-none font-bold uppercase">
            {{ product.name }}
          </span>
        </div>
      </div>
    </PvButton>
  </ClientOnly>
</template>
